<template>
  <div>
    <v-row>
      <v-col cols="4">
        <div class="grey lighten-4">
          <div class="image-box text-center pa-8 d-flex flex-column justify-center">
            <v-img
              v-if="_.get(product.images, 'length') > 0"
              :src="helpers.getProductImageUrl(product, 0)"
              contain
              max-height="250"
              width="100%"
            />
            <div v-else>
              <img src="/static/images/vaporshop-logo.jpg">
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="8">
        <div class="px-6">
          <div class="d-flex align-center justify-space-between">
            <h1 class="text-h5 font-weight-bold text-uppercase">{{ product.name }}</h1>
            <div class="d-flex align-center">
              <v-rating
                :value="product.rating / 2"
                background-color="orange lighten-3"
                class="mt-n1"
                dense
                half-increments
                half-icon="far fa-star-half-stroke"
                readonly
                color="orange"
                length="5"
                size="16"
              />
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-show="product.superStar"
                    v-bind="attrs"
                    :color="product.rating > 8 ? '#FFD700' : '#c0c0c0'"
                    size="24px"
                    class="ml-2"
                    v-on="on"
                  >fas fa-medal</v-icon>
                </template>
                <span>{{ $t('Super Star') }}</span>
              </v-tooltip>

            </div>
          </div>
          <div
            v-if="product.description && product.description.trim().length"
            class="text-body-1 text-justify grey--text text--darken-1 mt-2 "
            v-html="product.description" />
          <div
            v-else
            class="text-body-1 text-justify grey--text text--darken-1 mt-2"
          >
            <v-alert
              class="d-inline-block"
              color="orange"
              text
              dense
            >
              {{ $t('No {0}', [$t('Description')]) }}
            </v-alert>
          </div>

          <div class="mt-2">
            <template v-if="product.retailPromo">
              {{ product.retailPromo | money }} <span class="font-strikethrough text-caption">{{ product.retailPrice | money }}</span>
            </template>
            <span
              v-else
              class="text-h5 font-weight-bold"
            >
              {{ product.retailPrice | money }}
            </span>
          </div>
          <v-divider class="my-2 grey lighten-4" />
          <table
            width="100%"
            cellspacing="10"
          >
            <colgroup>
              <col width="15%">
              <col width="85%">
            </colgroup>
            <tr>
              <td class="font-weight-bold">{{ $t('Stock') }}</td>
              <td><stock-chip :value="_.get(product, 'storeStock.stock', 0)" /></td>
            </tr>
            <tr v-if="_.get(product.flavors, 'length')">
              <td class="font-weight-bold">{{ $t('Flavors') }}</td>
              <td
                class="d-flex"
                style="gap:10"
              >
                <v-chip
                  v-for="tag in product.flavors"
                  :key="tag.id"
                  class="mr-1"
                  color="primary"
                  label
                  small
                  text-color="white"
                  @click="$emit('selected-flavor', tag)"
                >
                  {{ tag.translations[$store.state.i18n.locale] || tag.name }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="product.pgVgRatio">
              <td class="font-weight-bold">{{ $t('VG/PG Ratio') }}</td>
              <td>{{ product.pgVgRatio }}</td>
            </tr>
          </table>
        </div>
      </v-col>
    </v-row>
    <template v-if="$wait.is('fetch alternatives')">
      <v-row
        class="relative text-center fill-height align-center"
        wrap
      >
        <v-col cols="12">
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          />
        </v-col>
      </v-row>
    </template>
    <template
      v-if="alternatives.length && !$wait.is('fetch alternatives')"
    >
      <!-- <v-divider class="divider my-2 grey lighten-4" /> -->
      <h3 class="text-h5 mt-5">{{ $t('Recommendations') }}</h3>
      <v-row class="mt-2">
        <v-col
          v-for="alternative in alternatives"
          v-show="!$wait.is('fetch alternatives')"
          :key="alternative.id"
          cols="3"
        >
          <v-card
            class="mx-auto fill-height d-flex flex-column"
            @click="$emit('selected-alternative', alternative)"
          >
            <div class="flex-grow-1">
              <div
                class="d-flex flex-column justify-center grey lighten-4 relative pa-5"
                style="height: 200px;"
              >
                <v-img
                  v-if="_.get(alternative.images, 'length') > 0"
                  :src="helpers.getProductImageUrl(alternative, 0)"
                  contain
                  max-height="160"
                  style="mix-blend-mode: multiply;"
                  width="100%"
                />
                <template v-else>
                  <v-img
                    contain
                    src="/static/images/vaporshop-logo.jpg"
                    max-height="160"
                    style="mix-blend-mode: multiply;"
                  />
                </template>
                <v-rating
                  :value="alternative.rating / 2"
                  background-color="orange lighten-3"
                  dense
                  half-increments
                  half-icon="far fa-star-half-stroke"
                  readonly
                  color="orange"
                  length="5"
                  size="10"
                  class="suggestion-rating"
                />
              </div>
              <v-card-title
                class="text-body-1 font-weight-black"
              > {{ alternative.name }}
              </v-card-title>
              <v-card-text>
                <div
                  class="text-body-2 mt-n2"
                >
                  <template v-if="alternative.pgVgRatio">{{ $t('VG/PG Ratio') }}: {{ alternative.pgVgRatio }}</template>&nbsp;
                </div>
              </v-card-text>
            </div>
            <v-card-actions class="flex-wrap">
              <template v-if="_.get(alternative.flavors, 'length')">
                <v-chip
                  v-for="tag in alternative.flavors"
                  :key="tag.id"
                  class="ma-1"
                  color="grey lighten-3"
                  label
                  small
                >
                  {{ tag.translations[$store.state.i18n.locale] || tag.name }}
                </v-chip>
              </template>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import StockChip from '@/components/chips/StockChip'
import productService from '@/services/products'
import camelcaseKeys from 'camelcase-keys'

export default {
  name: 'LiquidDetails',
  components: {
    StockChip
  },
  props: {
    productObject: {
      default: null,
      type: Object
    }
  },
  data: () => ({
    product: null,
    alternatives: []
  }),
  computed: {
    ...mapState(['cashRegister'])
  },
  watch: {
    productObject: {
      immediate: true,
      handler (productObject) {
        this.product = Object.assign({}, productObject)
        this.getAlternatives(this.product.id)
      }
    }
  },
  mounted () {
    document.addEventListener('keyup', this.keyUpListener)
  },

  beforeDestroy () {
    document.removeEventListener('keyup', this.keyUpListener)
  },
  beforeUpdate () {
    let productStock = this.$store.state.stocks.find(x => x.productId === this.product.id)
    if (productStock) this.product.storeStock = productStock
  },
  methods: {
    getAlternatives (productId) {
      this.alternatives = []
      this.$wait.start('fetch alternatives')
      productService.getLiquidAlternatives(productId)
        .then(data => { this.alternatives = camelcaseKeys(data.data.alternatives, { deep: true }) })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end('fetch alternatives'))
    },
    keyUpListener (event) {
      switch (event.which) {
        case 37: this.$emit('previous')
          break
        case 39: this.$emit('next')
          break
      }
    }
  }
}
</script>

<style scoped lang="scss">
.image-box {
  // min-width: 550px;
  height: 250px;
  border: 2px solid #e0e0e0;
  mix-blend-mode: multiply;
  box-sizing: content-box
}
.divider {
  border-width: 1px;
}

.suggestion-rating {
  position: absolute;
  top: 10px;
  right: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/vapevault/LiquidDetails.vue